/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Desktop → Stitch "Detalle de Pedido Desktop" (main flex-2 + rail w-96).
 * Mobile  → Stitch "Detalle de Pedido Mobile" (dot stepper + sticky CTA).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  View,
  ScrollView,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
  Pressable,
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { formatArPhoneDisplay, whatsAppHref } from '@shared/phone';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { getShellMainPanelWidth, useContentLayout } from '../../shared/layout/useContentLayout';
import { useShellScreenHeaderRefresh } from '../../shell/navigation/TabScreenRefreshContext';
import { useOrderDetailRealtime } from '../hooks/useOrderDetailRealtime';
import { showSuccess, showError } from '../../shared/ui/appToast'
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import {
  formatCountdown,
  formatExpiresAt,
  isExpired,
} from '@shared/orderChangeCountdown';
import {
  fetchOrderById,
  fetchOrderItemStocks,
  proposeOrderChange,
  updateOrderStatus,
  refundOrder,
  extendPreparingSla,
  CashSessionRequiredError,
} from '../infra/ordersRepo';
import {
  matchOrderToRider,
  fetchAssignmentForOrder,
  getRiderPayoutInfo,
  markRiderPayoutPaid,
} from '../infra/deliveryTrackingRepo';
import {
  ORDER_STATUS_LABELS,
  nextActionStatus,
  nextActionLabel,
  orderStatusSteps,
  orderStepperProgressIndex,
  canMerchantCancel,
  isPreparingDelayed,
  type CustomerOrderDetail,
  type CustomerOrderItemChange,
  type OrderStatus,
  type PickupType,
} from '../domain/orderTypes';
import { formatCancelReasonLabel } from '../domain/cancelReasonLabels';
import { resolveOrderItemsVisibility } from '../domain/orderItemsVisibility';
import {
  isMercadoPagoPaidOrder,
  isPayAtPickupOrder,
  needsCashSessionToComplete,
} from '../domain/orderPayment';
import { merchantDeliveryBadge } from '../domain/deliveryPhase';
import {
  InformMissingItemModal,
  type InformMissingProposal,
} from './components/InformMissingItemModal';
import { OrderCollectPaymentSheet } from './components/OrderCollectPaymentSheet';
import {
  formatCollectAndDeliverCta,
  type PickupCollectMethod,
} from '../domain/orderCompletePayment';
import {
  MercadoPagoQrPaymentModal,
  useMercadoPagoQrSession,
} from '../../mercadopago/public-api';
import { getCachedMpIntegrationStatus } from '../../settings/application/storefrontStatus';
import { OrderActivitySection } from './components/OrderActivitySection';
import { DeliveryAssignPicker } from './components/DeliveryAssignPicker';
import { MerchantDeliveryTrackingPanel } from './components/MerchantDeliveryTrackingPanel';
import { MerchantRiderPayoutBox } from './components/MerchantRiderPayoutBox';
import { RiderRatingPanel } from './components/RiderRatingPanel';
import { OrderComandaShareButton } from './components/OrderComandaShareButton';
import { OrderHandoffBlock } from './components/OrderHandoffBlock';
import { OrdersCashClosedBanner } from '../components/OrdersCashClosedBanner';
import { openSaleDetail } from '../../sales/public-api';
import {
  formatOrderDisplayCode,
  formatOrderDisplayCodeLabel,
  formatOrderItemOptionsLabel,
  type CustomerOrderItemRow,
} from '../domain/orderTypes';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { getMergedActiveCashSessionId } from '../../cash/infra-api';
import { formatQty } from '@shared/quantity';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { formatMoney } from '../../shared/format/public-api';

function itemDisplay(item: CustomerOrderItemRow) {
  const removed = item.item_status === 'removed';
  const replaced = item.item_status === 'replaced';
  const name =
    replaced && item.effective_product_name ? item.effective_product_name : item.product_name;
  const unitPrice =
    replaced && item.effective_unit_price != null ? item.effective_unit_price : item.unit_price;
  const lineTotal = removed
    ? 0
    : replaced && item.effective_line_total != null
      ? item.effective_line_total
      : item.line_total;
  const optionsLabel = formatOrderItemOptionsLabel(item.options);
  const lineNote = item.line_note?.trim() || '';
  return { removed, replaced, name, unitPrice, lineTotal, optionsLabel, lineNote };
}

/** Stitch desktop rail = Tailwind w-96. Desk layout needs rail + usable products column. */
const DESK_RAIL_WIDTH = 384;
/** Min main panel width for two-column desk (rail + products table). Below → mobile stack. */
const DESK_LAYOUT_MIN_PANEL = DESK_RAIL_WIDTH + 520;

function formatDiscountAmount(amount: number): string {
  return `−${formatMoney(amount)}`;
}

type OrderDiscountRow = { id: string; label: string; discount_amount: number };

/** Filas de promo sin duplicar discount_total cuando ya hay detalle. */
function resolveOrderDiscountRows(
  discounts: OrderDiscountRow[],
  discountTotal: number,
): OrderDiscountRow[] {
  if (discounts.length > 0) return discounts;
  if (discountTotal > 0) {
    return [{ id: '__discount_total', label: 'Descuento', discount_amount: discountTotal }];
  }
  return [];
}

/**
 * Desglose que cierra: list_price (neto + promos) → promos → envío → total.
 * Los line_total del pedido ya vienen netos de promo.
 */
function OrderMoneyBreakdown({
  itemsSubtotal,
  discounts,
  discountTotal,
  deliveryFee,
  totalAmount,
  variant,
  hasItems,
}: {
  itemsSubtotal: number;
  discounts: OrderDiscountRow[];
  discountTotal: number;
  deliveryFee: number | null | undefined;
  totalAmount: number;
  variant: 'desktop' | 'mobile';
  /** Sin ítems la fila "Productos" mostraría $0 junto a un total real: se oculta. */
  hasItems: boolean;
}) {
  const discountRows = resolveOrderDiscountRows(discounts, discountTotal);
  const discountAmount = discountRows.reduce((sum, d) => sum + Number(d.discount_amount || 0), 0);
  const productsSubtotal = itemsSubtotal + discountAmount;
  const showDelivery = deliveryFee != null && Number(deliveryFee) > 0;

  if (variant === 'mobile') {
    return (
      <View style={styles.mobileBreakdown}>
        {hasItems ? (
          <View style={styles.mobileBreakdownRow}>
            <Text style={styles.mobileBreakdownLabel}>Productos</Text>
            <Text style={styles.mobileBreakdownValue}>{formatMoney(productsSubtotal)}</Text>
          </View>
        ) : null}
        {discountRows.map((d) => (
          <View key={d.id} style={styles.mobileBreakdownRow}>
            <Text style={styles.mobileBreakdownPromoLabel} numberOfLines={2}>
              {d.label}
            </Text>
            <Text style={styles.mobileBreakdownPromoValue}>
              {formatDiscountAmount(d.discount_amount)}
            </Text>
          </View>
        ))}
        {showDelivery ? (
          <View style={styles.mobileBreakdownRow}>
            <Text style={styles.mobileBreakdownLabel}>Envío</Text>
            <Text style={styles.mobileBreakdownValue}>{formatMoney(Number(deliveryFee))}</Text>
          </View>
        ) : null}
        <View style={styles.mobileTotalRow}>
          <Text style={styles.mobileTotalLabel}>Total</Text>
          <Text style={styles.mobileTotalValue}>{formatMoney(totalAmount)}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.totalsCard}>
      {hasItems ? (
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>Productos</Text>
          <Text style={styles.totalsValue}>{formatMoney(productsSubtotal)}</Text>
        </View>
      ) : null}
      {discountRows.map((d) => (
        <View key={d.id} style={styles.totalsRow}>
          <Text style={styles.totalsDiscountLabel} numberOfLines={2}>
            {d.label}
          </Text>
          <Text style={styles.totalsDiscountValue}>{formatDiscountAmount(d.discount_amount)}</Text>
        </View>
      ))}
      {showDelivery ? (
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>Envío</Text>
          <Text style={styles.totalsValue}>{formatMoney(Number(deliveryFee))}</Text>
        </View>
      ) : null}
      <View style={styles.totalsDivider} />
      <View style={styles.totalsRowEnd}>
        <Text style={styles.totalsTotalLabel}>TOTAL</Text>
        <Text style={styles.totalsTotalValue}>{formatMoney(totalAmount)}</Text>
      </View>
    </View>
  );
}

const STEP_ICONS: Record<OrderStatus, keyof typeof Ionicons.glyphMap> = {
  pending: 'time-outline',
  confirmed: 'radio-button-on',
  preparing: 'restaurant-outline',
  ready: 'cube-outline',
  dispatched: 'bicycle-outline',
  delivered: 'home-outline',
  picked_up: 'checkmark-circle-outline',
  cancelled: 'close-circle-outline',
};

function buildWhatsAppUrl(
  phone: string,
  status: OrderStatus,
  orderShortId: string,
  opts?: { resolutionStatus?: string | null; storeName?: string | null },
): string {
  const storeName = (opts?.storeName ?? '').trim() || 'el local';
  const cancelledMsg =
    opts?.resolutionStatus === 'expired'
      ? `Tu pedido #${orderShortId} se canceló. No hubo respuesta a tiempo por un producto que faltaba.`
      : `Tu pedido #${orderShortId} se canceló.`;
  const messages: Record<OrderStatus, string> = {
    pending: `Hola, te escribimos de ${storeName} por tu pedido #${orderShortId}.`,
    confirmed: `Confirmamos tu pedido #${orderShortId}. Lo estamos armando.`,
    preparing: `Tu pedido #${orderShortId} se está armando.`,
    ready: `Tu pedido #${orderShortId} está listo para retirar.`,
    dispatched: `Tu pedido #${orderShortId} va en camino.`,
    delivered: `Tu pedido #${orderShortId} ya te llegó. Gracias.`,
    picked_up: `Gracias. Tu pedido #${orderShortId} ya está entregado.`,
    cancelled: cancelledMsg,
  };
  const msg = messages[status];
  if (!msg) return '';
  return whatsAppHref(phone, msg) ?? '';
}

function dateTimeLabel(iso: string): string {
  return new Date(iso).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function elapsedLabel(iso: string): string {
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'Ahora';
  if (min < 60) return `Hace ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `Hace ${hr} h`;
  return `Hace ${Math.floor(hr / 24)} d`;
}

function changeTypeLabel(type: CustomerOrderItemChange['change_type']): string {
  switch (type) {
    case 'replace':
      return 'Reemplazo';
    case 'reduce_quantity':
      return 'Bajar unidades';
    case 'remove':
      return 'Eliminar';
    default:
      return type;
  }
}

function describeChange(change: CustomerOrderItemChange): string {
  if (change.change_type === 'remove') {
    return `Eliminar ${change.original_product_name} (${change.original_quantity} u.)`;
  }
  if (change.change_type === 'reduce_quantity') {
    return `${change.original_product_name}: ${change.original_quantity} → ${change.proposed_quantity} u.`;
  }
  const name = change.proposed_product_name ?? 'otro producto';
  return `${change.original_product_name} → ${name} (${change.proposed_quantity} u.)`;
}

function paymentStatusLabel(status: string | null): string {
  if (status === 'paid' || status === 'approved') return 'Aprobado';
  if (status === 'confirmed') return 'Confirmado';
  if (status === 'refunded') return 'Reembolsado';
  if (status === 'failed') return 'Fallido';
  if (status === 'pending') return 'Pendiente';
  if (status === 'cancelled') return 'Anulado';
  if (status === 'created') return 'Iniciado';
  if (status === 'expired') return 'Vencido';
  if (!status) return '—';
  return status;
}

function paymentStatusColor(status: string | null): string {
  if (status === 'paid' || status === 'approved' || status === 'confirmed') return ticketColors.ok;
  if (status === 'refunded' || status === 'failed' || status === 'cancelled') return ticketColors.bad;
  return ticketColors.gold;
}

function statusTone(status: OrderStatus): { bg: string; fg: string; dot: string; border: string } {
  if (status === 'cancelled') {
    return {
      bg: ticketColors.paper,
      fg: ticketColors.bad,
      dot: ticketColors.bad,
      border: ticketColors.bad,
    };
  }
  if (status === 'confirmed' || status === 'preparing' || status === 'ready') {
    return {
      bg: ticketColors.paper,
      fg: ticketColors.ok,
      dot: ticketColors.ok,
      border: ticketColors.ok,
    };
  }
  if (status === 'picked_up' || status === 'delivered' || status === 'dispatched') {
    return {
      bg: ticketColors.paper,
      fg: ticketColors.ink2,
      dot: ticketColors.ink2,
      border: ticketColors.rule,
    };
  }
  return {
    bg: ticketColors.gold,
    fg: ticketColors.goldInk,
    dot: ticketColors.goldInk,
    border: ticketColors.gold,
  };
}

function customerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
}

function DesktopStepper({
  status,
  pickupType,
  cancelReason,
}: {
  status: OrderStatus;
  pickupType: PickupType;
  cancelReason?: string | null;
}) {
  if (status === 'cancelled') {
    return (
      <View style={styles.stepperCard}>
        <View style={styles.cancelledStep}>
          <Ionicons name="close-circle" size={22} color={ticketColors.bad} />
          <View style={{ flex: 1 }}>
            <Text style={styles.cancelledStepText}>Pedido cancelado</Text>
            <Text style={styles.cancelledReasonText}>{formatCancelReasonLabel(cancelReason)}</Text>
          </View>
        </View>
      </View>
    );
  }

  const steps = orderStatusSteps(pickupType);
  const currentIdx = orderStepperProgressIndex(status, pickupType);

  return (
    <View style={styles.stepperCard}>
      <View style={styles.stepperRow}>
        {steps.map((step, idx) => {
          const done = idx < currentIdx;
          const current = idx === currentIdx;
          return (
            <React.Fragment key={step}>
              {idx > 0 ? (
                <View
                  style={[
                    styles.stepConnector,
                    idx <= currentIdx ? styles.stepConnectorDone : null,
                  ]}
                />
              ) : null}
              <View style={styles.stepItem}>
                <View
                  style={[
                    styles.stepCircle,
                    done && styles.stepCircleDone,
                    current && styles.stepCircleCurrent,
                  ]}
                >
                  {done ? (
                    <Ionicons name="checkmark" size={18} color={ticketColors.goldInk} />
                  ) : (
                    <Ionicons
                      name={STEP_ICONS[step]}
                      size={18}
                      color={current ? ticketColors.goldInk : ticketColors.ink3}
                    />
                  )}
                </View>
                <Text
                  style={[
                    styles.stepLabel,
                    current && styles.stepLabelCurrent,
                    done && styles.stepLabelDone,
                  ]}
                  numberOfLines={1}
                >
                  {ORDER_STATUS_LABELS[step]}
                </Text>
              </View>
            </React.Fragment>
          );
        })}
      </View>
    </View>
  );
}

function MobileStepper({
  status,
  pickupType,
  fulfillment,
  when,
  cancelReason,
}: {
  status: OrderStatus;
  pickupType: PickupType;
  fulfillment: string;
  when: string;
  cancelReason?: string | null;
}) {
  if (status === 'cancelled') {
    return (
      <View style={styles.mobileStepperCard}>
        <View style={styles.cancelledStep}>
          <Ionicons name="close-circle" size={20} color={ticketColors.bad} />
          <View style={{ flex: 1 }}>
            <Text style={styles.cancelledStepText}>Pedido cancelado</Text>
            <Text style={styles.cancelledReasonText}>{formatCancelReasonLabel(cancelReason)}</Text>
          </View>
        </View>
      </View>
    );
  }

  const steps = orderStatusSteps(pickupType);
  const currentIdx = orderStepperProgressIndex(status, pickupType);
  const fillAt = Math.min(currentIdx, steps.length - 1);
  const fillFlex = Math.max(fillAt, 0);
  const restFlex = Math.max(steps.length - 1 - fillAt, 0);

  return (
    <View style={styles.mobileStepperCard}>
      <View style={styles.dotTrackWrap}>
        <View style={styles.dotTrackLine}>
          <View style={[styles.dotTrackFill, { flex: fillFlex || 0.01 }]} />
          <View style={{ flex: restFlex || 0.01 }} />
        </View>
        <View style={styles.dotRow}>
          {steps.map((step, idx) => {
            const done = idx < currentIdx;
            const current = idx === currentIdx;
            const active = done || current;
            return (
              <View key={step} style={styles.dotItem}>
                <View
                  style={[
                    styles.dot,
                    done && styles.dotDone,
                    current && styles.dotCurrent,
                    !active && styles.dotIdle,
                  ]}
                />
                <Text
                  style={[
                    styles.dotLabel,
                    current && styles.dotLabelCurrent,
                    !active && styles.dotLabelIdle,
                  ]}
                  numberOfLines={1}
                >
                  {ORDER_STATUS_LABELS[step]}
                </Text>
              </View>
            );
          })}
        </View>
      </View>
      <View style={styles.mobileStepperMeta}>
        <Text style={styles.mobileStepperMetaText}>{fulfillment}</Text>
        <Text style={styles.mobileStepperMetaText}>{when}</Text>
      </View>
    </View>
  );
}

export const OnlineOrderDetailScreen = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const orderId = route.params?.orderId as string;
  const { width } = useContentLayout();
  // Use shell panel width (window − sidebar), not raw window — intermediate web
  // with sidebar was picking desk layout and crushing the products table.
  const isWide = getShellMainPanelWidth(width) >= DESK_LAYOUT_MIN_PANEL;
  const branches = useSessionStore((s) => s.branches);
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const canViewOrders = useModulePermission('Orders', 'view');
  const canOperateOrders = useModulePermission('Orders', 'edit');
  const canVoidOrders = useModulePermission('Orders', 'void');
  const canRefundOrders = useModulePermission('Orders', 'refund');
  const canViewContact = useModulePermission('Orders', 'view_contact');
  const deliveryGate = useCapabilityGate('delivery_fulfillment', {
    title: 'Pedidos online no disponibles',
    subtitle: 'Este rubro no tiene pedidos online.',
  });
  const { confirm, ConfirmModal } = useOperationalConfirm();

  const [order, setOrder] = useState<CustomerOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [missingItem, setMissingItem] = useState<CustomerOrderDetail['items'][number] | null>(null);
  const [proposalSending, setProposalSending] = useState(false);
  const [itemStocks, setItemStocks] = useState<Record<string, number>>({});
  const [, setTick] = useState(0);
  const [assignment, setAssignment] = useState<{
    id: string;
    status: string;
    payout_status: string;
    rider_earning: number;
    rider_id: string | null;
    rider_name?: string | null;
  } | null>(null);
  const [payoutInfo, setPayoutInfo] = useState<{
    amount: number;
    alias: string | null;
    cbu: string | null;
    init_point: string | null;
    rider_name: string | null;
    message?: string;
  } | null>(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [activeCashSessionId, setActiveCashSessionId] = useState<string | null | undefined>(
    undefined,
  );
  const [collectSheetVisible, setCollectSheetVisible] = useState(false);
  const [qrSaleId, setQrSaleId] = useState<string | null>(null);
  const [mpQrAvailable, setMpQrAvailable] = useState(false);

  const loadOrder = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const detail = await fetchOrderById(orderId);
      setOrder(detail);
      if (detail?.pickup_type === 'delivery') {
        try {
          const asg = await fetchAssignmentForOrder(orderId);
          setAssignment(asg);
        } catch {
          setAssignment(null);
        }
      } else {
        setAssignment(null);
      }
      if (detail && detail.status === 'pending') {
        const ids = detail.items.map((i) => i.product_id);
        try {
          const stocks = await fetchOrderItemStocks(detail.branch_id, ids);
          setItemStocks(stocks);
        } catch {
          setItemStocks({});
        }
      } else {
        setItemStocks({});
      }
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo cargar el pedido');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void loadOrder();
  }, [loadOrder]);

  const onHeaderRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await loadOrder({ silent: true });
    } finally {
      setRefreshing(false);
    }
  }, [loadOrder]);

  useShellScreenHeaderRefresh('OnlineOrderDetail', onHeaderRefresh);

  const orderRefreshControl = (
    <RefreshControl
      refreshing={refreshing}
      onRefresh={() => void onHeaderRefresh()}
      colors={[ticketColors.gold]}
      tintColor={ticketColors.gold}
    />
  );

  useOrderDetailRealtime(orderId, () => {
    void loadOrder({ silent: true });
  }, {
    // Live while matching / en ruta; terminal states still cheap to poll lightly
    pollMs:
      order?.status === 'picked_up' || order?.status === 'cancelled'
        ? null
        : order?.pickup_type === 'delivery' &&
            ['ready', 'dispatched', 'delivered'].includes(order.status)
          ? 10_000
          : 20_000,
  });

  // Panel tracks delivery_tracking faster than order row — pull order when leg goes terminal
  // so stepper + RiderRatingPanel appear without F5.
  const onDeliveryLegChange = useCallback(
    (leg: string | null) => {
      if (leg === 'delivered' || leg === 'failed' || leg === 'cancelled') {
        void loadOrder({ silent: true });
      }
    },
    [loadOrder],
  );

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  const handleAction = useCallback(
    async (next: OrderStatus) => {
      if (!order) return;
      setActionLoading(true);
      try {
        if (
          next === 'dispatched' &&
          order.pickup_type === 'delivery' &&
          order.delivery_fulfillment === 'platform_rider'
        ) {
          const result = await matchOrderToRider(order.id);
          showSuccess(
            'Buscando repartidores',
            result.offers_sent > 0
              ? `Mandamos ${result.offers_sent} ofertas. Gana ${formatMoney(result.rider_earning)}.`
              : 'No hay repartidores cerca. Probá de nuevo en un rato.',
          );
          await loadOrder({ silent: true });
          return;
        }

        let cashSessionId: string | null | undefined;
        if (needsCashSessionToComplete(order, next)) {
          cashSessionId =
            activeCashSessionId ??
            (await getMergedActiveCashSessionId(order.branch_id, { force: true }));
          if (!cashSessionId) {
            showError(
              'Caja cerrada',
              'Abrí caja para cobrar este pedido en efectivo.',
            );
            return;
          }
        }

        const result = await updateOrderStatus(
          order.id,
          next,
          cashSessionId ? { cashSessionId } : undefined,
        );
        if (result && result.phase === 'qr_pending') {
          return;
        }
        setOrder((prev) => (prev ? { ...prev, status: next } : prev));
        showSuccess('Pedido actualizado', ORDER_STATUS_LABELS[next]);
      } catch (e: any) {
        if (e instanceof CashSessionRequiredError) {
          showError('Caja cerrada', e.message);
        } else {
          presentUnexpectedFailure({ module: 'orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo actualizar' });
        }
      } finally {
        setActionLoading(false);
      }
    },
    [order, loadOrder, activeCashSessionId],
  );

  const handleCollectMethod = useCallback(
    async (method: PickupCollectMethod) => {
      if (!order) return;
      if (method === 'cash' || method === 'transfer') {
        const cashSessionId =
          activeCashSessionId ??
          (await getMergedActiveCashSessionId(order.branch_id, { force: true }));
        if (!cashSessionId) {
          setCollectSheetVisible(false);
          showError('Caja cerrada', 'Abrí caja para cobrar este pedido.');
          navigateInShell(navigation, 'CashManagement');
          return;
        }
        setActionLoading(true);
        try {
          const result = await updateOrderStatus(order.id, 'picked_up', {
            cashSessionId,
            paymentMethodId: method,
            completeMode: 'collect',
          });
          if (result?.phase === 'qr_pending') return;
          setCollectSheetVisible(false);
          setOrder((prev) =>
            prev ? { ...prev, status: 'picked_up', payment_status: 'paid' } : prev,
          );
          showSuccess('Pedido cobrado', 'Ya está entregado y asentado en caja.');
        } catch (e: unknown) {
          if (e instanceof CashSessionRequiredError) {
            showError('Caja cerrada', e.message);
            navigateInShell(navigation, 'CashManagement');
          } else {
            presentUnexpectedFailure({
              module: 'orders',
              op: 'error',
              error: e,
              title: 'Error',
              userMessage: 'No se pudo cobrar el pedido',
            });
          }
        } finally {
          setActionLoading(false);
        }
        return;
      }

      setActionLoading(true);
      try {
        const result = await updateOrderStatus(order.id, 'picked_up', {
          paymentMethodId: 'mercadopago_qr',
          completeMode: 'qr_init',
        });
        if (!result?.saleId) {
          throw new Error('No se pudo crear el cobro QR');
        }
        setCollectSheetVisible(false);
        setQrSaleId(result.saleId);
      } catch (e: unknown) {
        presentUnexpectedFailure({
          module: 'orders',
          op: 'error',
          error: e,
          title: 'Error',
          userMessage: 'No se pudo generar el QR',
        });
      } finally {
        setActionLoading(false);
      }
    },
    [order, activeCashSessionId, navigation],
  );

  const finalizeQrCollect = useCallback(async () => {
    if (!order) return;
    try {
      await updateOrderStatus(order.id, 'picked_up', {
        paymentMethodId: 'mercadopago_qr',
        completeMode: 'qr_finalize',
      });
      setQrSaleId(null);
      setOrder((prev) =>
        prev ? { ...prev, status: 'picked_up', payment_status: 'paid' } : prev,
      );
      showSuccess('Pedido cobrado', 'El QR se acreditó y el pedido quedó entregado.');
    } catch (e: unknown) {
      presentUnexpectedFailure({
        module: 'orders',
        op: 'error',
        error: e,
        title: 'Error',
        userMessage: 'El QR se pagó pero no se pudo marcar el retiro',
      });
    }
  }, [order]);

  const qrSession = useMercadoPagoQrSession({
    saleId: qrSaleId,
    visible: qrSaleId != null,
    amount: order?.total_amount ?? null,
    onApproved: () => {
      void finalizeQrCollect();
    },
  });

  useEffect(() => {
    if (!order?.branch_id) {
      setMpQrAvailable(false);
      return;
    }
    let cancelled = false;
    void getCachedMpIntegrationStatus(order.branch_id)
      .then((status) => {
        if (!cancelled) setMpQrAvailable(status.oauth_connected && status.pos_configured);
      })
      .catch(() => {
        if (!cancelled) setMpQrAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [order?.branch_id]);

  const handleRetryMatch = useCallback(async () => {
    if (!order) return;
    setActionLoading(true);
    try {
      const result = await matchOrderToRider(order.id);
      showSuccess(
        'Buscando de nuevo',
        result.offers_sent > 0
          ? `Mandamos ${result.offers_sent} ofertas.`
          : 'Sin repartidores. Probá de nuevo más tarde.',
      );
      await loadOrder({ silent: true });
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo buscar de nuevo' });
    } finally {
      setActionLoading(false);
    }
  }, [order, loadOrder]);

  const handleLoadPayout = useCallback(async () => {
    if (!assignment?.id) return;
    setPayoutLoading(true);
    try {
      const info = await getRiderPayoutInfo(assignment.id);
      setPayoutInfo(info);
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudieron cargar los datos de pago' });
    } finally {
      setPayoutLoading(false);
    }
  }, [assignment?.id]);

  const handleMarkPayoutPaid = useCallback(async () => {
    if (!assignment?.id) return;
    setPayoutLoading(true);
    try {
      await markRiderPayoutPaid(assignment.id);
      showSuccess('Pago marcado', 'Esperá a que el repartidor confirme.');
      await loadOrder({ silent: true });
      setPayoutInfo(null);
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo marcar pagado' });
    } finally {
      setPayoutLoading(false);
    }
  }, [assignment?.id, loadOrder]);

  const sendProposal = useCallback(
    async (proposal: InformMissingProposal) => {
      if (!order || !missingItem) return;
      setProposalSending(true);
      try {
        await proposeOrderChange({
          orderId: order.id,
          orderItemId: missingItem.id,
          changeType: proposal.changeType,
          proposedProductId: proposal.proposedProductId,
          proposedQuantity: proposal.proposedQuantity,
        });
        setMissingItem(null);
        await loadOrder();
        showSuccess('Propuesta enviada', 'El cliente tiene que aceptar el cambio.');
      } catch (err: unknown) {
        presentUnexpectedFailure({ module: 'orders', op: 'error', error: err, title: 'Error', userMessage: 'No se pudo enviar la propuesta' });
      } finally {
        setProposalSending(false);
      }
    },
    [loadOrder, missingItem, order],
  );

  const handleMissingItem = useCallback(
    (item: CustomerOrderDetail['items'][number]) => {
      if (
        !order ||
        !['pending', 'confirmed', 'preparing'].includes(order.status) ||
        order.resolution_status === 'pending_customer'
      ) {
        return;
      }
      setMissingItem(item);
    },
    [order],
  );

  const handleCancel = useCallback(async () => {
    if (!order || !canMerchantCancel(order.status)) return;
    const wasPaidOnline = order.payment_provider === 'mercadopago' && order.payment_status === 'paid';
    const message = wasPaidOnline
      ? 'Este pedido ya está pagado con Mercado Pago. ¿Cancelás y le devolvés la plata?'
      : '¿Seguro que querés cancelar este pedido?';
    const ok = await confirm({
      title: 'Cancelar pedido',
      message,
      confirmLabel: 'Sí, cancelar',
      destructive: true,
    });
    if (!ok) return;
    if (!order) return;
    setActionLoading(true);
    try {
      if (wasPaidOnline) {
        await refundOrder(order.id);
      } else {
        await updateOrderStatus(order.id, 'cancelled');
      }
      setOrder((prev) =>
        prev
          ? {
              ...prev,
              status: 'cancelled',
              cancel_reason: 'merchant',
              cancelled_at: new Date().toISOString(),
              payment_status: wasPaidOnline ? 'refunded' : prev.payment_status,
            }
          : prev,
      );
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo cancelar el pedido' });
    } finally {
      setActionLoading(false);
    }
  }, [confirm, order]);

  const handleExtendSla = useCallback(async () => {
    if (!order || order.status !== 'preparing') return;
    setActionLoading(true);
    try {
      const next = await extendPreparingSla(order.id, 15);
      setOrder((prev) =>
        prev
          ? {
              ...prev,
              sla_extended_until: next.sla_extended_until,
              estimated_ready_at: next.estimated_ready_at,
            }
          : prev,
      );
      showSuccess('Tiempo extendido', '+15 minutos');
    } catch (e: any) {
      presentUnexpectedFailure({ module: 'orders', op: 'error', error: e, title: 'Error', userMessage: 'No se pudo extender el tiempo' });
    } finally {
      setActionLoading(false);
    }
  }, [order]);

  if (!deliveryGate.allowed) {
    return <ShellContentFrame style={styles.center}>{deliveryGate.blocked}</ShellContentFrame>;
  }

  if (loading) {
    return (
      <ShellContentFrame style={styles.center}>
        <ActivityIndicator size="large" color={ticketColors.gold} />
      </ShellContentFrame>
    );
  }

  if (!canViewOrders) {
    return (
      <ShellContentFrame style={styles.center}>
        <ModuleEmptyState
          title="No podés ver Pedidos"
          subtitle="Pedile al dueño que te dé permiso."
        />
      </ShellContentFrame>
    );
  }

  if (error || !order) {
    return (
      <ShellContentFrame style={styles.center}>
        <Text style={styles.errorText}>{error ?? 'Pedido no encontrado'}</Text>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Text style={styles.backBtnText}>Volver</Text>
        </TouchableOpacity>
      </ShellContentFrame>
    );
  }

  const hasPendingChange = order.resolution_status === 'pending_customer';
  const pendingChanges = order.changes.filter((c) => c.status === 'pending');
  const pendingDeadline = order.customer_action_deadline
    ?? pendingChanges[0]?.expires_at
    ?? null;
  const pendingExpired = pendingDeadline ? isExpired(pendingDeadline) : false;
  const nextStatus = hasPendingChange || !canOperateOrders
    ? null
    : nextActionStatus(order.status, order.pickup_type, order.delivery_fulfillment);
  const nextLabel = hasPendingChange || !canOperateOrders
    ? null
    : nextActionLabel(order.status, order.pickup_type, order.delivery_fulfillment, {
        payment_provider: order.payment_provider,
        payment_status: order.payment_status,
      });
  const collectCtaLabel =
    nextLabel === 'Cobrar y entregar'
      ? formatCollectAndDeliverCta(Number(order.total_amount))
      : nextLabel;
  const unpaidCollect =
    nextStatus === 'picked_up' &&
    needsCashSessionToComplete(order, nextStatus);
  const tone = statusTone(order.status);
  const isMpPaid = isMercadoPagoPaidOrder(order);
  const payAtPickup = isPayAtPickupOrder(order);
  const canReportMissing =
    canOperateOrders &&
    ['pending', 'confirmed', 'preparing'].includes(order.status) &&
    !hasPendingChange &&
    !isMpPaid;
  const showCancelCta =
    canVoidOrders &&
    canMerchantCancel(order.status) &&
    !hasPendingChange &&
    (!isMpPaid || canRefundOrders);
  const preparingDelayed = isPreparingDelayed(order);
  const showActions = order.status !== 'picked_up' && order.status !== 'cancelled';
  const needsRiderPayout =
    !!assignment &&
    ['due', 'overdue', 'pending_confirm', 'disputed'].includes(assignment.payout_status);
  const fulfillment =
    order.pickup_type === 'pickup'
      ? 'Retiro en el local'
      : order.delivery_fulfillment === 'platform_rider'
        ? 'Lo lleva un repartidor'
        : 'Lo lleva el local';
  const shortId = formatOrderDisplayCode(order.display_code, order.id);
  const orderRefLabel = formatOrderDisplayCodeLabel(order.display_code, order.id);
  const storeName =
    branches.find((b) => b.id === order.branch_id)?.name?.trim() || 'Sucursal';
  const orderOnOtherBranch =
    !!currentBranchId && currentBranchId !== order.branch_id;
  const itemsSubtotal = order.items.reduce((sum, item) => {
    const { lineTotal } = itemDisplay(item);
    return sum + lineTotal;
  }, 0);
  const itemsView = resolveOrderItemsVisibility(order.items);
  const openWhatsApp = () => {
    if (!canViewContact || !order.customer_phone) return;
    void Linking.openURL(
      buildWhatsAppUrl(order.customer_phone, order.status, shortId, {
        resolutionStatus: order.resolution_status,
        storeName,
      }),
    );
  };

  const openCashManagement = () => {
    navigateInShell(navigation, 'CashManagement');
  };

  const alerts = (
    <>
      {order.contains_restricted ? (
        <View style={styles.dniBanner}>
          <Ionicons name="warning-outline" size={16} color="#FFB74D" />
          <Text style={styles.dniBannerText}>Verificar DNI · +18</Text>
        </View>
      ) : null}
      {hasPendingChange ? (
        <View style={[styles.pendingBox, pendingExpired && styles.pendingBoxExpired]}>
          <Text style={styles.pendingTitle}>
            {pendingExpired ? 'Propuesta vencida' : 'Esperando al cliente'}
          </Text>
          {pendingChanges.map((change) => (
            <Text key={change.id} style={styles.pendingDesc}>
              {changeTypeLabel(change.change_type)}: {describeChange(change)}
            </Text>
          ))}
          {pendingDeadline ? (
            <Text style={[styles.pendingDeadline, pendingExpired && styles.pendingDeadlineUrgent]}>
              {pendingExpired
                ? 'No respondió. El pedido se cancela solo.'
                : `${formatCountdown(pendingDeadline)} · vence ${formatExpiresAt(pendingDeadline)}. Si no responde, se cancela.`}
            </Text>
          ) : null}
        </View>
      ) : null}

      {isMpPaid && ['confirmed', 'preparing'].includes(order.status) ? (
        <View style={styles.mpPaidHint}>
          <Text style={styles.mpPaidHintText}>
            Este ya está pagado. Si falta algo, resolvelo a mano. No se puede mandar un cambio automático.
          </Text>
        </View>
      ) : null}

      <OrdersCashClosedBanner
        variant="detail"
        onlineRelevant={payAtPickup && showActions}
        onActiveSessionChange={setActiveCashSessionId}
      />
    </>
  );

  const missingModal = (
    <InformMissingItemModal
      visible={missingItem !== null}
      item={missingItem}
      branchId={order.branch_id}
      organizationId={order.organization_id}
      sending={proposalSending}
      onClose={() => {
        if (!proposalSending) setMissingItem(null);
      }}
      onSubmit={(proposal) => void sendProposal(proposal)}
    />
  );

  const collectModals = (
    <>
      <OrderCollectPaymentSheet
        visible={collectSheetVisible}
        amount={Number(order.total_amount)}
        showMercadoPagoQr={mpQrAvailable}
        sending={actionLoading}
        onClose={() => setCollectSheetVisible(false)}
        onSelect={(method) => void handleCollectMethod(method)}
      />
      <MercadoPagoQrPaymentModal
        visible={qrSaleId != null}
        phase={qrSession.phase}
        loading={qrSession.loading}
        error={qrSession.error}
        qrData={qrSession.qrData}
        qrUrl={qrSession.qrUrl}
        amount={qrSession.amount}
        saleRef={null}
        expiresAt={qrSession.expiresAt}
        countdownSeconds={qrSession.countdownSeconds}
        fiscalInvoiceLabel={qrSession.fiscalInvoiceLabel}
        autoCloseProgress={qrSession.autoCloseProgress}
        lastMpQrSaleId={qrSaleId}
        onClose={() => setQrSaleId(null)}
        onRegenerate={qrSession.regenerateQr}
      />
    </>
  );

  // ── Desktop ──────────────────────────────────────────────────────────
  if (isWide) {
    return (
      <ShellContentFrame style={styles.container}>
        <View style={styles.desk}>
          <ScrollView
            style={styles.deskMain}
            contentContainerStyle={styles.deskMainContent}
            showsVerticalScrollIndicator={false}
            refreshControl={orderRefreshControl}
          >
            <View style={styles.deskHeader}>
              <View style={styles.deskHeaderLeft}>
                <Text style={styles.deskTitle} numberOfLines={1}>
                  Pedido {orderRefLabel}
                </Text>
                <Text style={styles.branchMeta} numberOfLines={1}>
                  {storeName}
                  {orderOnOtherBranch ? ' · otra sucursal' : ''}
                </Text>
                <View style={[styles.statusPill, { backgroundColor: tone.bg, borderColor: tone.border }]}>
                  <View style={[styles.statusDot, { backgroundColor: tone.dot }]} />
                  <Text style={[styles.statusPillText, { color: tone.fg }]}>
                    {(
                      merchantDeliveryBadge(
                        order.status,
                        order.pickup_type,
                        order.delivery_fulfillment,
                        assignment?.status,
                      ) ?? ORDER_STATUS_LABELS[order.status]
                    ).toUpperCase()}
                  </Text>
                </View>
                {preparingDelayed ? (
                  <View style={[styles.statusPill, { backgroundColor: ticketColors.paper, borderColor: ticketColors.bad }]}>
                    <Text style={[styles.statusPillText, { color: ticketColors.bad }]}>Demorado</Text>
                  </View>
                ) : null}
              </View>
            </View>

            <DesktopStepper
              status={order.status}
              pickupType={order.pickup_type}
              cancelReason={order.cancel_reason}
            />

            <OrderHandoffBlock
              order={order}
              riderAssignmentStatus={assignment?.status}
            />

            <View style={styles.productsBlock}>
              <View style={styles.productsHead}>
                <Text style={styles.productsTitle}>Productos</Text>
                <Text style={styles.productsMeta}>
                  {itemsView.hasItems
                    ? `${order.items.length} ${order.items.length === 1 ? 'ítem' : 'ítems'}`
                    : 'Sin ítems'}
                </Text>
              </View>
              <View style={styles.tableCard}>
                {itemsView.hasItems ? (
                <View style={styles.tableHead}>
                  <Text style={[styles.th, styles.colItem]}>Item</Text>
                  <Text style={[styles.th, styles.colQty]}>Cant.</Text>
                  <Text style={[styles.th, styles.colPrice]}>Precio</Text>
                  <Text style={[styles.th, styles.colSub]}>Subtotal</Text>
                  {canReportMissing ? (
                    <Text style={[styles.th, styles.colAction]} />
                  ) : null}
                </View>
                ) : null}
                {order.items.map((item) => {
                  const d = itemDisplay(item);
                  const canMissing = canReportMissing && !d.removed;
                  return (
                  <View key={item.id} style={[styles.tableRow, d.removed && styles.rowRemoved]}>
                    <View style={[styles.colItem, styles.itemCell]}>
                      <View style={styles.itemIcon}>
                        <Ionicons name="bag-handle-outline" size={20} color={ticketColors.gold} />
                      </View>
                      <View style={styles.itemText}>
                        <Text
                          style={[styles.itemName, d.removed && styles.itemNameRemoved]}
                          numberOfLines={2}
                        >
                          {d.name}
                        </Text>
                        {d.optionsLabel ? (
                          <Text style={styles.itemHint} numberOfLines={2}>{d.optionsLabel}</Text>
                        ) : null}
                        {d.lineNote ? (
                          <Text style={styles.itemHint} numberOfLines={2}>Nota: {d.lineNote}</Text>
                        ) : null}
                        {d.replaced ? (
                          <Text style={styles.itemHint}>Reemplazo de {item.product_name}</Text>
                        ) : null}
                        {d.removed ? (
                          <Text style={styles.itemHintWarn}>Salió del pedido</Text>
                        ) : null}
                        {order.status === 'pending' && !d.removed ? (
                          <Text
                            style={
                              (itemStocks[item.product_id] ?? 0) < item.quantity
                                ? styles.itemHintWarn
                                : styles.itemHint
                            }
                          >
                            Stock {(itemStocks[item.product_id] ?? 0)} · pide {formatQty(item.quantity, item.unit_of_measure)}
                            {(itemStocks[item.product_id] ?? 0) < item.quantity
                              ? ' · no alcanza'
                              : ''}
                          </Text>
                        ) : null}
                      </View>
                    </View>
                    <Text style={[styles.tdQty, styles.colQty]}>{formatQty(item.quantity, item.unit_of_measure)}</Text>
                    <Text style={[styles.tdMuted, styles.colPrice]}>
                      {formatMoney(d.unitPrice)}
                    </Text>
                    <Text style={[styles.td, styles.colSub]}>
                      {formatMoney(d.lineTotal)}
                    </Text>
                    {canReportMissing ? (
                      <View style={styles.colAction}>
                        {canMissing ? (
                          <TouchableOpacity
                            onPress={() => handleMissingItem(item)}
                            style={styles.faltanteChip}
                            accessibilityRole="button"
                            accessibilityLabel={`Avisar faltante de ${item.product_name}`}
                          >
                            <Text style={styles.faltanteChipText}>Faltante</Text>
                          </TouchableOpacity>
                        ) : null}
                      </View>
                    ) : null}
                  </View>
                  );
                })}
                {itemsView.showEmptyWarning ? (
                  <View style={styles.itemsEmptyBox} testID="order.items.empty">
                    <Ionicons name="alert-circle-outline" size={18} color="#FFB74D" />
                    <View style={styles.itemsEmptyTextWrap}>
                      <Text style={styles.itemsEmptyText}>
                        Este pedido no tiene ítems cargados.
                      </Text>
                      <Text style={styles.itemsEmptyHint}>
                        El total quedó registrado, pero no hay detalle de qué pidió el cliente.
                      </Text>
                    </View>
                  </View>
                ) : null}
              </View>
              <View style={styles.totalsWrap}>
                <OrderMoneyBreakdown
                  itemsSubtotal={itemsSubtotal}
                  discounts={order.discounts}
                  discountTotal={order.discount_total ?? 0}
                  deliveryFee={order.delivery_fee}
                  totalAmount={order.total_amount}
                  variant="desktop"
                  hasItems={itemsView.showProductsRow}
                />
              </View>
              <OrderActivitySection events={order.events} />
            </View>
          </ScrollView>

          <View style={styles.deskRail}>
            <ScrollView
              style={styles.deskRailScroll}
              contentContainerStyle={styles.deskRailContent}
              showsVerticalScrollIndicator={false}
              refreshControl={orderRefreshControl}
            >
              <View style={styles.glassCard}>
                <Text style={styles.sectionLabel}>Cliente</Text>
                <View style={styles.customerRow}>
                  <View style={styles.avatar}>
                    <Text style={styles.avatarText}>{customerInitials(order.customer_name)}</Text>
                  </View>
                  <View style={styles.customerText}>
                    <Text style={styles.customerName}>{order.customer_name}</Text>
                    {canViewContact && order.customer_phone ? (
                      <Text style={styles.customerPhone}>
                        {formatArPhoneDisplay(order.customer_phone)}
                      </Text>
                    ) : null}
                  </View>
                </View>
                {canViewContact && order.customer_phone ? (
                  <TouchableOpacity style={styles.whatsappBtn} onPress={openWhatsApp}>
                    <Ionicons name="logo-whatsapp" size={18} color="#25D366" />
                    <Text style={styles.whatsappBtnText}>WhatsApp</Text>
                  </TouchableOpacity>
                ) : null}
              </View>

              {alerts}

              <View style={[styles.glassCard, styles.notesCard]}>
                <View style={styles.sectionLabelRow}>
                  <Ionicons name="document-text-outline" size={14} color={ticketColors.ink3} />
                  <Text style={[styles.sectionLabel, styles.sectionLabelInline]}>Notas</Text>
                </View>
                {order.notes ? (
                  <View style={styles.notesBox}>
                    <Text style={styles.notesText}>“{order.notes}”</Text>
                  </View>
                ) : (
                  <Text style={styles.notesEmpty}>El cliente no dejó notas</Text>
                )}

                <Text style={[styles.sectionLabel, styles.deliveryTitle]}>Entrega</Text>
                <View style={styles.metaList}>
                  <View style={styles.metaRow}>
                    <Ionicons
                      name={order.pickup_type === 'pickup' ? 'storefront-outline' : 'location-outline'}
                      size={18}
                      color={ticketColors.gold}
                    />
                    <Text style={styles.metaText}>
                      {fulfillment}
                      {order.pickup_time_slot ? ` · ${order.pickup_time_slot}` : ''}
                    </Text>
                  </View>
                  {order.pickup_type === 'delivery' && order.delivery_address ? (
                    <View style={styles.metaRow}>
                      <Ionicons name="navigate-outline" size={18} color={ticketColors.gold} />
                      <Text style={styles.metaText}>{order.delivery_address}</Text>
                    </View>
                  ) : null}
                  {order.pickup_type === 'delivery' && order.delivery_eta_minutes != null ? (
                    <View style={styles.metaRow}>
                      <Ionicons name="bicycle-outline" size={18} color={ticketColors.gold} />
                      <Text style={styles.metaText}>Llega en {order.delivery_eta_minutes} min</Text>
                    </View>
                  ) : null}
                  {(order.rider_tip_amount ?? 0) > 0 ? (
                    <View style={styles.metaRow}>
                      <Ionicons name="heart-outline" size={18} color={ticketColors.gold} />
                      <Text style={styles.metaText}>Propina del repartidor {formatMoney(order.rider_tip_amount ?? 0)}</Text>
                    </View>
                  ) : null}
                  <View style={styles.metaRow}>
                    <Ionicons name="time-outline" size={18} color={ticketColors.gold} />
                    <Text style={styles.metaText}>Recibido: {timeLabel(order.created_at)}</Text>
                  </View>
                  {order.estimated_ready_at ? (
                    <View style={styles.metaRow}>
                      <Ionicons name="hourglass-outline" size={18} color={ticketColors.gold} />
                      <Text style={styles.metaText}>
                        Listo a las {timeLabel(order.estimated_ready_at)}
                      </Text>
                    </View>
                  ) : null}
                  <View style={styles.metaRow}>
                    <Ionicons name="card-outline" size={18} color={ticketColors.gold} />
                    <View style={styles.paymentBadgeCol}>
                      {isMpPaid ? (
                        <View style={[styles.paymentBadge, styles.paymentBadgePaid]}>
                          <Text style={styles.paymentBadgePaidText}>Ya pagó (MP)</Text>
                        </View>
                      ) : payAtPickup ? (
                        <View style={[styles.paymentBadge, styles.paymentBadgeCash]}>
                          <Text style={styles.paymentBadgeCashText}>Cobrar en caja</Text>
                        </View>
                      ) : (
                        <Text style={styles.metaText}>
                          Mercado Pago ·{' '}
                          <Text style={{ color: paymentStatusColor(order.payment_status) }}>
                            {paymentStatusLabel(order.payment_status)}
                          </Text>
                        </Text>
                      )}
                    </View>
                  </View>
                </View>
                {order.pickup_type === 'delivery' &&
                ['dispatched', 'ready', 'delivered', 'picked_up'].includes(order.status) ? (
                  <MerchantDeliveryTrackingPanel
                    orderId={order.id}
                    branchId={order.branch_id}
                    isPlatformRider={order.delivery_fulfillment === 'platform_rider'}
                    deliveryLat={order.delivery_lat}
                    deliveryLng={order.delivery_lng}
                    deliveryAddress={order.delivery_address}
                    displayCode={order.display_code}
                    onLegChange={onDeliveryLegChange}
                  />
                ) : null}
                {order.pickup_type === 'delivery' &&
                order.delivery_fulfillment === 'platform_rider' &&
                order.status === 'delivered' ? (
                  <RiderRatingPanel orderId={order.id} />
                ) : null}
                <View style={{ marginTop: 8, gap: 8 }}>
                  {order.sale_id ? (
                    <Pressable
                      onPress={() => openSaleDetail(navigation, { saleId: order.sale_id! })}
                      style={({ pressed }) => [
                        styles.viewSaleBtn,
                        order.status === 'picked_up' ? styles.viewSaleBtnPrimary : null,
                        pressed ? { opacity: 0.85 } : null,
                      ]}
                      accessibilityRole="button"
                      accessibilityLabel="Ver venta"
                    >
                      <Text
                        style={[
                          styles.viewSaleBtnText,
                          order.status === 'picked_up' ? styles.viewSaleBtnTextPrimary : null,
                        ]}
                      >
                        Ver venta
                      </Text>
                    </Pressable>
                  ) : null}
                  <OrderComandaShareButton order={order} />
                  <Pressable
                    onPress={() => navigation.navigate('OrderMessageThread', { orderId: order.id })}
                    style={({ pressed }) => [
                      {
                        paddingVertical: 12,
                        paddingHorizontal: 14,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: '#E5E7EB',
                        backgroundColor: pressed ? '#F3F4F6' : '#fff',
                        alignItems: 'center',
                      },
                    ]}
                  >
                    <Text style={{ fontFamily: ticketFonts.sans, fontWeight: '600', fontSize: 14, color: ticketColors.ink }}>
                      Mensajes del pedido
                    </Text>
                  </Pressable>
                </View>
              </View>

              {showActions ? (
                <View style={styles.deskActions}>
                  {nextStatus && collectCtaLabel ? (
                    <TouchableOpacity
                      style={[styles.primaryCtaDesk, actionLoading && styles.btnDisabled]}
                      onPress={() => {
                        if (unpaidCollect) {
                          setCollectSheetVisible(true);
                          return;
                        }
                        void handleAction(nextStatus);
                      }}
                      disabled={actionLoading}
                      testID={`orders.detail.action.${nextStatus}`}
                    >
                      {actionLoading ? (
                        <ActivityIndicator color={ticketColors.goldInk} />
                      ) : (
                        <>
                          <Text style={styles.primaryCtaText}>{collectCtaLabel.toUpperCase()}</Text>
                          <View style={styles.primaryCtaSub}>
                            <Ionicons name="chevron-forward" size={14} color={ticketColors.goldInk} />
                          </View>
                        </>
                      )}
                    </TouchableOpacity>
                  ) : null}
                  {canOperateOrders &&
                  order.pickup_type === 'delivery' &&
                  order.delivery_fulfillment === 'platform_rider' &&
                  order.status === 'ready' &&
                  assignment ? (
                    <TouchableOpacity
                      style={[styles.rejectCta, actionLoading && styles.btnDisabled]}
                      onPress={() => void handleRetryMatch()}
                      disabled={actionLoading}
                    >
                      <Text style={styles.rejectCtaText}>Buscar otro repartidor</Text>
                    </TouchableOpacity>
                  ) : null}
                  {canOperateOrders &&
                  order.pickup_type === 'delivery' &&
                  order.delivery_fulfillment !== 'platform_rider' &&
                  order.status === 'ready' ? (
                    <DeliveryAssignPicker
                      orderId={order.id}
                      onDispatched={() => void loadOrder({ silent: true })}
                    />
                  ) : null}
                  {needsRiderPayout && assignment ? (
                    <MerchantRiderPayoutBox
                      assignment={assignment}
                      tipAmount={order?.rider_tip_amount ?? 0}
                      payoutInfo={payoutInfo}
                      payoutLoading={payoutLoading}
                      onLoadPayout={() => void handleLoadPayout()}
                      onMarkPaid={() => void handleMarkPayoutPaid()}
                      variant="desk"
                    />
                  ) : null}
                  {canOperateOrders && order.status === 'preparing' ? (
                    <TouchableOpacity
                      style={styles.rejectCta}
                      onPress={() => void handleExtendSla()}
                      disabled={actionLoading}
                    >
                      <Text style={styles.rejectCtaText}>
                        {preparingDelayed ? '+15 minutos (demorado)' : '+15 minutos'}
                      </Text>
                    </TouchableOpacity>
                  ) : null}
                  {showCancelCta ? (
                    <TouchableOpacity
                      style={styles.rejectCta}
                      onPress={() => void handleCancel()}
                      disabled={actionLoading}
                    >
                      <Text style={styles.rejectCtaText}>
                        {order.status === 'pending' ? 'Rechazar pedido' : 'Cancelar pedido'}
                      </Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              ) : null}
            </ScrollView>
          </View>
        </View>
        {missingModal}
        {collectModals}
        {ConfirmModal}
      </ShellContentFrame>
    );
  }

  // ── Mobile (Stitch Detalle de Pedido Mobile) ─────────────────────────
  return (
    <ShellContentFrame style={styles.container}>
      <ScrollView
        contentContainerStyle={[
          styles.mobileScroll,
          showActions && nextStatus ? styles.mobileScrollWithCta : null,
        ]}
        showsVerticalScrollIndicator={false}
        refreshControl={orderRefreshControl}
      >
        <View style={styles.mobileHeader}>
          <View style={styles.mobileHeaderCenter}>
            <Text style={styles.mobileTitle} numberOfLines={1}>
              Pedido {orderRefLabel}
            </Text>
            <Text style={styles.branchMeta} numberOfLines={1}>
              {storeName}
              {orderOnOtherBranch ? ' · otra sucursal' : ''}
            </Text>
            <View style={styles.mobileHeaderMeta}>
              <View
                style={[
                  styles.statusPillCompact,
                  { backgroundColor: tone.bg, borderColor: tone.border },
                ]}
              >
                <Text style={[styles.statusPillText, { color: tone.fg }]}>
                  {(
                    merchantDeliveryBadge(
                      order.status,
                      order.pickup_type,
                      order.delivery_fulfillment,
                      assignment?.status,
                    ) ?? ORDER_STATUS_LABELS[order.status]
                  ).toUpperCase()}
                </Text>
              </View>
              {isMpPaid ? (
                <View style={[styles.paymentBadge, styles.paymentBadgePaid]}>
                  <Text style={styles.paymentBadgePaidText}>Ya pagó (MP)</Text>
                </View>
              ) : payAtPickup ? (
                <View style={[styles.paymentBadge, styles.paymentBadgeCash]}>
                  <Text style={styles.paymentBadgeCashText}>Cobrar en caja</Text>
                </View>
              ) : null}
              {preparingDelayed ? (
                <View
                  style={[
                    styles.statusPillCompact,
                    { backgroundColor: ticketColors.paper, borderColor: ticketColors.bad },
                  ]}
                >
                  <Text style={[styles.statusPillText, { color: ticketColors.bad }]}>Demorado</Text>
                </View>
              ) : null}
              <Text style={styles.elapsedText}>{elapsedLabel(order.created_at)}</Text>
            </View>
          </View>
        </View>

        <MobileStepper
          status={order.status}
          pickupType={order.pickup_type}
          fulfillment={fulfillment}
          when={dateTimeLabel(order.created_at)}
          cancelReason={order.cancel_reason}
        />

        {alerts}

        <OrderHandoffBlock
          order={order}
          riderAssignmentStatus={assignment?.status}
        />

        <View style={styles.mobileCard}>
          <View style={styles.mobileCardHead}>
            <Text style={styles.mobileCardLabel}>Productos</Text>
            <Text style={styles.mobileCardCount}>
              {itemsView.hasItems
                ? `${order.items.length} ${order.items.length === 1 ? 'ítem' : 'ítems'}`
                : 'Sin ítems'}
            </Text>
          </View>
          <View style={styles.mobileProductsBody}>
            {order.items.map((item) => {
              const d = itemDisplay(item);
              const canMissing = canReportMissing && !d.removed;
              return (
              <View key={item.id} style={[styles.mobileProductRow, d.removed && styles.rowRemoved]}>
                <View style={styles.mobileProductLeft}>
                  <Text style={[styles.mobileProductName, d.removed && styles.itemNameRemoved]}>
                    {d.name}
                  </Text>
                  {d.optionsLabel ? (
                    <Text style={styles.itemHint} numberOfLines={2}>{d.optionsLabel}</Text>
                  ) : null}
                  {d.lineNote ? (
                    <Text style={styles.itemHint} numberOfLines={2}>Nota: {d.lineNote}</Text>
                  ) : null}
                  {d.replaced ? (
                    <Text style={styles.itemHint}>Reemplazo de {item.product_name}</Text>
                  ) : null}
                  {d.removed ? (
                    <Text style={styles.itemHintWarn}>Salió del pedido</Text>
                  ) : (
                    <Text style={styles.mobileProductQty}>
                      {formatQty(item.quantity, item.unit_of_measure)} × {formatMoney(d.unitPrice)}
                    </Text>
                  )}
                  {order.status === 'pending' && !d.removed ? (
                    <Text
                      style={
                        (itemStocks[item.product_id] ?? 0) < item.quantity
                          ? styles.itemHintWarn
                          : styles.itemHint
                      }
                    >
                      Stock {(itemStocks[item.product_id] ?? 0)} · pide {formatQty(item.quantity, item.unit_of_measure)}
                      {(itemStocks[item.product_id] ?? 0) < item.quantity
                        ? ' · no alcanza'
                        : ''}
                    </Text>
                  ) : null}
                </View>
                <View style={styles.mobileProductRight}>
                  <Text style={styles.mobileProductTotal}>
                    {formatMoney(d.lineTotal)}
                  </Text>
                  {canMissing ? (
                    <TouchableOpacity
                      onPress={() => handleMissingItem(item)}
                      style={styles.faltanteChip}
                      accessibilityRole="button"
                      accessibilityLabel={`Avisar faltante de ${item.product_name}`}
                    >
                      <Text style={styles.faltanteChipText}>Faltante</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              </View>
              );
            })}
            {itemsView.showEmptyWarning ? (
              <View
                style={[styles.itemsEmptyBox, { margin: 0 }]}
                testID="order.items.empty"
              >
                <Ionicons name="alert-circle-outline" size={18} color="#FFB74D" />
                <View style={styles.itemsEmptyTextWrap}>
                  <Text style={styles.itemsEmptyText}>
                    Este pedido no tiene ítems cargados.
                  </Text>
                  <Text style={styles.itemsEmptyHint}>
                    El total quedó registrado, pero no hay detalle de qué pidió el cliente.
                  </Text>
                </View>
              </View>
            ) : null}
          </View>
          <OrderMoneyBreakdown
            itemsSubtotal={itemsSubtotal}
            discounts={order.discounts}
            discountTotal={order.discount_total ?? 0}
            deliveryFee={order.delivery_fee}
            totalAmount={order.total_amount}
            variant="mobile"
            hasItems={itemsView.showProductsRow}
          />
        </View>

        <View style={styles.mobileCard}>
          <Text style={styles.mobileCardLabel}>Cliente</Text>
          <View style={styles.customerRow}>
            <View style={styles.avatarSoft}>
              <Ionicons name="person" size={22} color={ticketColors.ok} />
            </View>
            <View style={styles.customerText}>
              <Text style={styles.customerName}>{order.customer_name}</Text>
              {canViewContact && order.customer_phone ? (
                <Text style={styles.customerPhone}>
                  {formatArPhoneDisplay(order.customer_phone)}
                </Text>
              ) : null}
            </View>
          </View>
          {canViewContact && order.customer_phone ? (
            <TouchableOpacity style={styles.whatsappBtn} onPress={openWhatsApp}>
              <Ionicons name="logo-whatsapp" size={18} color="#25D366" />
              <Text style={styles.whatsappBtnText}>WhatsApp</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        <View style={styles.mobileCard}>
          <Text style={styles.mobileCardLabel}>Notas</Text>
          {order.notes ? (
            <View style={styles.notesBoxSoft}>
              <Text style={styles.notesText}>“{order.notes}”</Text>
            </View>
          ) : (
            <Text style={styles.notesEmpty}>El cliente no dejó notas</Text>
          )}
        </View>

        {order.pickup_type === 'delivery' ? (
          <View style={styles.mobileCard}>
            <Text style={styles.mobileCardLabel}>Entrega</Text>
            <Text style={styles.customerPhone}>{fulfillment}</Text>
            {order.delivery_address ? (
              <Text style={styles.customerName}>{order.delivery_address}</Text>
            ) : null}
            {order.delivery_eta_minutes != null ? (
              <Text style={styles.customerPhone}>Llega en {order.delivery_eta_minutes} min</Text>
            ) : null}
            {(order.rider_tip_amount ?? 0) > 0 ? (
              <Text style={styles.customerPhone}>Propina del repartidor {formatMoney(order.rider_tip_amount ?? 0)}</Text>
            ) : null}
            {order.pickup_type === 'delivery' &&
            ['dispatched', 'ready', 'delivered', 'picked_up'].includes(order.status) ? (
              <MerchantDeliveryTrackingPanel
                orderId={order.id}
                branchId={order.branch_id}
                isPlatformRider={order.delivery_fulfillment === 'platform_rider'}
                deliveryLat={order.delivery_lat}
                deliveryLng={order.delivery_lng}
                deliveryAddress={order.delivery_address}
                displayCode={order.display_code}
                onLegChange={onDeliveryLegChange}
              />
            ) : null}
            {order.pickup_type === 'delivery' &&
            order.delivery_fulfillment === 'platform_rider' &&
            order.status === 'delivered' ? (
              <RiderRatingPanel orderId={order.id} />
            ) : null}
            <View style={{ marginTop: 8, gap: 8 }}>
              {order.sale_id ? (
                <TouchableOpacity
                  style={[
                    styles.whatsappBtn,
                    order.status === 'picked_up' ? styles.viewSaleBtnPrimary : null,
                  ]}
                  onPress={() => openSaleDetail(navigation, { saleId: order.sale_id! })}
                  accessibilityRole="button"
                  accessibilityLabel="Ver venta"
                >
                  <Ionicons
                    name="receipt-outline"
                    size={18}
                    color={order.status === 'picked_up' ? ticketColors.goldInk : ticketColors.gold}
                  />
                  <Text
                    style={[
                      styles.whatsappBtnText,
                      order.status === 'picked_up' ? styles.viewSaleBtnTextPrimary : null,
                    ]}
                  >
                    Ver venta
                  </Text>
                </TouchableOpacity>
              ) : null}
              <OrderComandaShareButton order={order} />
              <TouchableOpacity
                style={styles.whatsappBtn}
                onPress={() => navigation.navigate('OrderMessageThread', { orderId: order.id })}
              >
                <Ionicons name="chatbubble-outline" size={18} color={ticketColors.gold} />
                <Text style={styles.whatsappBtnText}>Mensajes del pedido</Text>
              </TouchableOpacity>
            </View>
            {order.status !== 'delivered' &&
            order.status !== 'picked_up' &&
            order.delivery_lat != null &&
            order.delivery_lng != null ? (
              <TouchableOpacity
                style={styles.whatsappBtn}
                onPress={() =>
                  void Linking.openURL(
                    `https://www.google.com/maps/dir/?api=1&destination=${order.delivery_lat},${order.delivery_lng}`,
                  )
                }
              >
                <Ionicons name="map-outline" size={18} color={ticketColors.gold} />
                <Text style={styles.whatsappBtnText}>Abrir Maps</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ) : null}

        <OrderActivitySection events={order.events} />

        {canOperateOrders &&
        order.pickup_type === 'delivery' &&
        order.delivery_fulfillment === 'platform_rider' &&
        order.status === 'ready' &&
        assignment ? (
          <TouchableOpacity
            style={styles.rejectCta}
            onPress={() => void handleRetryMatch()}
            disabled={actionLoading}
          >
            <Text style={styles.rejectCtaText}>Buscar otro repartidor</Text>
          </TouchableOpacity>
        ) : null}
        {canOperateOrders &&
        order.pickup_type === 'delivery' &&
        order.delivery_fulfillment !== 'platform_rider' &&
        order.status === 'ready' ? (
          <DeliveryAssignPicker
            orderId={order.id}
            onDispatched={() => void loadOrder({ silent: true })}
          />
        ) : null}
        {needsRiderPayout && assignment ? (
          <MerchantRiderPayoutBox
            assignment={assignment}
            tipAmount={order.rider_tip_amount ?? 0}
            payoutInfo={payoutInfo}
            payoutLoading={payoutLoading}
            onLoadPayout={() => void handleLoadPayout()}
            onMarkPaid={() => void handleMarkPayoutPaid()}
            variant="mobile"
          />
        ) : null}

        {canOperateOrders && order.status === 'preparing' ? (
          <TouchableOpacity
            style={styles.rejectCta}
            onPress={() => void handleExtendSla()}
            disabled={actionLoading}
          >
            <Text style={styles.rejectCtaText}>
              {preparingDelayed ? '+15 minutos (demorado)' : '+15 minutos'}
            </Text>
          </TouchableOpacity>
        ) : null}
        {showCancelCta ? (
          <TouchableOpacity
            style={styles.rejectCta}
            onPress={() => void handleCancel()}
            disabled={actionLoading}
          >
            <Text style={styles.rejectCtaText}>
              {order.status === 'pending' ? 'Rechazar pedido' : 'Cancelar pedido'}
            </Text>
          </TouchableOpacity>
        ) : null}
      </ScrollView>

      {showActions && nextStatus && nextLabel ? (
        <View style={styles.mobileCtaBar}>
          <TouchableOpacity
            style={[styles.primaryCtaMobile, actionLoading && styles.btnDisabled]}
            onPress={() => {
              if (unpaidCollect) {
                setCollectSheetVisible(true);
                return;
              }
              void handleAction(nextStatus);
            }}
            disabled={actionLoading}
            testID={`orders.detail.action.${nextStatus}`}
          >
            {actionLoading ? (
              <ActivityIndicator color={ticketColors.goldInk} />
            ) : (
              <Text style={styles.primaryCtaMobileText}>{collectCtaLabel?.toUpperCase()}</Text>
            )}
          </TouchableOpacity>
        </View>
      ) : null}

      {missingModal}
      {collectModals}
      {ConfirmModal}
    </ShellContentFrame>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 24 },

  // Desktop: Stitch main flex-[2] + rail w-96
  desk: {
    flex: 1,
    flexDirection: 'row',
    minHeight: 0,
  },
  deskMain: {
    flex: 2,
    minWidth: 0,
  },
  deskMainContent: {
    padding: 24,
    paddingBottom: 40,
    gap: 20,
  },
  deskRail: {
    width: DESK_RAIL_WIDTH,
    flexShrink: 0,
    borderLeftWidth: StyleSheet.hairlineWidth,
    borderLeftColor: ticketColors.rule,
    backgroundColor: '#070F18',
  },
  deskRailScroll: {
    flex: 1,
  },
  deskRailContent: {
    padding: 24,
    paddingBottom: 28,
    gap: 16,
    flexGrow: 1,
  },
  deskHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  deskHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  deskTitle: {
    color: ticketColors.ink,
    fontSize: 24,
    fontWeight: '800',
    letterSpacing: -0.3,
    flexShrink: 1,
  },
  branchMeta: {
    color: ticketColors.ink3,
    fontSize: 13,
    fontWeight: '600',
    marginTop: 2,
  },
  deskActions: {
    marginTop: 'auto',
    gap: 10,
    paddingTop: 8,
  },

  // Mobile
  mobileScroll: {
    padding: 16,
    paddingBottom: 24,
    gap: 12,
  },
  mobileScrollWithCta: {
    paddingBottom: 100,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  mobileHeaderCenter: {
    flex: 1,
    minWidth: 0,
  },
  mobileTitle: {
    color: ticketColors.ink,
    fontSize: 20,
    fontWeight: '700',
  },
  mobileHeaderMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
  },
  elapsedText: {
    color: ticketColors.ink3,
    fontSize: 11,
    fontWeight: '500',
  },
  statusPillCompact: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    borderWidth: 1,
  },
  mobileCard: {
    ...ticketSurfaces.paperSheet,
    padding: 16,
    gap: 12,
  },
  mobileCardHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: -16,
    marginTop: -16,
    marginBottom: 0,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: ticketColors.paper2,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  mobileCardLabel: {
    color: ticketColors.ink3,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  mobileCardCount: {
    color: ticketColors.gold,
    fontSize: 11,
    fontWeight: '600',
  },
  mobileProductsBody: {
    gap: 14,
  },
  mobileProductRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  mobileProductLeft: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  mobileProductRight: {
    alignItems: 'flex-end',
    gap: 6,
  },
  mobileProductName: {
    color: ticketColors.ink,
    fontSize: 16,
    fontWeight: '700',
  },
  mobileProductQty: {
    color: ticketColors.ink3,
    fontSize: 14,
  },
  mobileProductTotal: {
    color: ticketColors.ink,
    fontSize: 16,
    fontWeight: '700',
  },
  faltanteChip: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 1,
    borderColor: ticketColors.gold,
  },
  faltanteChipText: {
    color: ticketColors.gold,
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.4,
  },
  mobileBreakdown: {
    marginHorizontal: -16,
    marginBottom: -16,
    marginTop: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    paddingTop: 4,
  },
  mobileBreakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  mobileBreakdownLabel: {
    color: ticketColors.ink2,
    fontSize: 14,
    fontWeight: '600',
    flex: 1,
  },
  mobileBreakdownValue: {
    color: ticketColors.ink,
    fontSize: 14,
    fontWeight: '700',
  },
  mobileBreakdownPromoLabel: {
    color: ticketColors.ink3,
    fontSize: 13,
    fontWeight: '600',
    flex: 1,
  },
  mobileBreakdownPromoValue: {
    color: ticketColors.ok,
    fontSize: 13,
    fontWeight: '700',
  },
  mobileTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: ticketColors.rule,
    backgroundColor: ticketColors.paper2,
  },
  mobileTotalLabel: {
    color: ticketColors.ink,
    fontSize: 16,
    fontWeight: '700',
  },
  mobileTotalValue: {
    color: ticketColors.gold,
    fontSize: 22,
    fontWeight: '800',
  },
  mobileCtaBar: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: 16,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    backgroundColor: 'rgba(13,20,29,0.92)',
  },
  primaryCtaMobile: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    height: 56,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryCtaMobileText: {
    color: ticketColors.goldInk,
    fontSize: 16,
    fontWeight: '900',
    letterSpacing: 0.8,
  },

  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  statusPillText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.5,
  },

  // Desktop stepper
  stepperCard: {
    ...ticketSurfaces.paperSheet,
    paddingVertical: 16,
    paddingHorizontal: 12,
  },
  stepperRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stepItem: {
    alignItems: 'center',
    gap: 8,
    width: 64,
  },
  stepCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepCircleDone: { backgroundColor: ticketColors.ok },
  stepCircleCurrent: { backgroundColor: ticketColors.gold },
  stepConnector: {
    flex: 1,
    height: 2,
    backgroundColor: ticketColors.paper2,
    marginTop: 19,
    marginHorizontal: 2,
  },
  stepConnectorDone: { backgroundColor: ticketColors.ok },
  stepLabel: {
    color: ticketColors.ink3,
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },
  stepLabelCurrent: { color: ticketColors.gold, fontWeight: '800' },
  stepLabelDone: { color: ticketColors.ink },
  cancelledStep: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 4,
  },
  cancelledStepText: {
    color: ticketColors.bad,
    fontSize: 14,
    fontWeight: '700',
  },
  cancelledReasonText: {
    color: ticketColors.ink2,
    fontSize: 12,
    fontWeight: '600',
    marginTop: 2,
  },

  // Mobile stepper (dots)
  mobileStepperCard: {
    ...ticketSurfaces.paperSheet,
    padding: 16,
  },
  dotTrackWrap: {
    position: 'relative',
    paddingTop: 2,
    paddingBottom: 4,
  },
  dotTrackLine: {
    position: 'absolute',
    top: 8,
    left: 28,
    right: 28,
    height: 2,
    flexDirection: 'row',
    backgroundColor: ticketColors.paper2,
    overflow: 'hidden',
  },
  dotTrackFill: {
    height: 2,
    backgroundColor: ticketColors.ok,
  },
  dotRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  dotItem: {
    alignItems: 'center',
    gap: 8,
    width: 56,
  },
  dot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 3,
    borderColor: ticketColors.paper,
  },
  dotDone: { backgroundColor: ticketColors.gold },
  dotCurrent: { backgroundColor: ticketColors.ok },
  dotIdle: { backgroundColor: ticketColors.ink3, opacity: 0.45 },
  dotLabel: {
    color: ticketColors.ink2,
    fontSize: 10,
    fontWeight: '700',
    textAlign: 'center',
  },
  dotLabelCurrent: { color: ticketColors.ok },
  dotLabelIdle: { opacity: 0.5 },
  mobileStepperMeta: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.rule,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  mobileStepperMetaText: {
    color: ticketColors.ink3,
    fontSize: 11,
  },

  productsBlock: { gap: 12 },
  productsHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  productsTitle: {
    color: ticketColors.gold,
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: 0.8,
  },
  productsMeta: {
    color: ticketColors.ink3,
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  tableCard: {
    ...ticketSurfaces.paperSheet,
  },
  tableHead: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: ticketColors.paper2,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  th: {
    color: ticketColors.ink3,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
    gap: 8,
  },
  colItem: { flex: 1, minWidth: 0 },
  colQty: { width: 56, textAlign: 'center' },
  colPrice: { width: 88, textAlign: 'right' },
  colSub: { width: 96, textAlign: 'right' },
  colAction: {
    width: 96,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  itemCell: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  itemIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemText: { flex: 1, minWidth: 0, gap: 2 },
  itemName: {
    color: ticketColors.ink,
    fontSize: 16,
    fontWeight: '700',
  },
  itemNameRemoved: {
    textDecorationLine: 'line-through',
    color: ticketColors.ink3,
  },
  itemHint: {
    color: ticketColors.ink3,
    fontSize: 12,
  },
  itemHintWarn: {
    color: '#FFB74D',
    fontSize: 12,
    fontWeight: '600',
  },
  itemsEmptyBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    margin: 16,
    backgroundColor: 'rgba(255,152,0,0.15)',
    borderWidth: 1,
    borderColor: '#FF9800',
    borderRadius: 10,
    padding: 12,
  },
  itemsEmptyTextWrap: { flex: 1, gap: 2 },
  itemsEmptyText: { color: '#FFB74D', fontSize: 13, fontWeight: '700' },
  itemsEmptyHint: { color: ticketColors.ink3, fontSize: 12, lineHeight: 16 },
  rowRemoved: {
    opacity: 0.7,
  },
  td: { color: ticketColors.ink, fontSize: 14, fontWeight: '600' },
  tdMuted: { color: ticketColors.ink3, fontSize: 14 },
  tdQty: {
    color: ticketColors.gold,
    fontSize: 16,
    fontWeight: '800',
    textAlign: 'center',
  },
  totalsWrap: { alignItems: 'flex-end' },
  totalsCard: {
    ...ticketSurfaces.paperSheet,
    minWidth: 260,
    padding: 16,
    gap: 8,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalsLabel: { color: ticketColors.ink3, fontSize: 12, fontWeight: '600' },
  totalsValue: { color: ticketColors.ink, fontSize: 14, fontWeight: '600' },
  totalsDiscountLabel: {
    color: ticketColors.ink3,
    fontSize: 12,
    fontWeight: '600',
    flex: 1,
    paddingRight: 8,
  },
  totalsDiscountValue: { color: ticketColors.gold, fontSize: 13, fontWeight: '700' },
  totalsDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: ticketColors.rule,
    marginVertical: 4,
  },
  totalsRowEnd: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  totalsTotalLabel: { color: ticketColors.gold, fontSize: 16, fontWeight: '800' },
  totalsTotalValue: { color: ticketColors.gold, fontSize: 24, fontWeight: '900' },

  glassCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    padding: 16,
  },
  notesCard: { flexGrow: 1 },
  sectionLabel: {
    color: ticketColors.ink3,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  sectionLabelInline: { marginBottom: 0 },
  sectionLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 12,
  },
  customerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 2,
    borderColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.paper2,
  },
  avatarSoft: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(61,214,140,0.15)',
  },
  avatarText: { color: ticketColors.gold, fontSize: 16, fontWeight: '800' },
  customerText: { flex: 1, minWidth: 0, gap: 2 },
  customerName: { color: ticketColors.ink, fontSize: 16, fontWeight: '700' },
  customerPhone: { color: ticketColors.ink3, fontSize: 13 },
  whatsappBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 12,
    borderWidth: 1,
    borderColor: 'rgba(37,211,102,0.35)',
    backgroundColor: 'rgba(37,211,102,0.08)',
    borderRadius: 10,
    paddingVertical: 12,
  },
  whatsappBtnText: { color: '#25D366', fontSize: 14, fontWeight: '700' },
  viewSaleBtn: {
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
  },
  viewSaleBtnPrimary: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  viewSaleBtnText: {
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
    fontSize: 14,
    color: ticketColors.ink,
  },
  viewSaleBtnTextPrimary: {
    color: ticketColors.goldInk,
    fontWeight: '700',
  },
  notesBox: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    borderLeftWidth: 3,
    borderLeftColor: ticketColors.gold,
    padding: 12,
  },
  notesBoxSoft: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    padding: 12,
  },
  notesText: {
    color: ticketColors.ink,
    fontSize: 14,
    fontStyle: 'italic',
    lineHeight: 20,
  },
  notesEmpty: { color: ticketColors.ink3, fontSize: 13 },
  deliveryTitle: { marginTop: 20, marginBottom: 12 },
  metaList: { gap: 12 },
  metaRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  paymentBadgeCol: { flex: 1, gap: 4 },
  paymentBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
  },
  paymentBadgePaid: {
    backgroundColor: ticketColors.paper,
    borderColor: ticketColors.ok,
  },
  paymentBadgePaidText: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ok,
    fontFamily: ticketFonts.sans,
  },
  paymentBadgeCash: {
    backgroundColor: ticketColors.paper,
    borderColor: ticketColors.gold,
  },
  paymentBadgeCashText: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  metaText: { color: ticketColors.ink, fontSize: 14, flex: 1, lineHeight: 20 },

  dniBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255,152,0,0.15)',
    borderWidth: 1,
    borderColor: '#FF9800',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  dniBannerText: { color: '#FFB74D', fontSize: 13, fontWeight: '700' },
  pendingBox: {
    backgroundColor: ticketColors.brandSoft,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.gold,
    borderRadius: 10,
    padding: 12,
    gap: 6,
  },
  pendingBoxExpired: {
    borderColor: ticketColors.bad,
    backgroundColor: 'rgba(255,107,107,0.12)',
  },
  pendingTitle: { color: ticketColors.gold, fontSize: 14, fontWeight: '800' },
  pendingDesc: { color: ticketColors.ink, fontSize: 13, lineHeight: 18 },
  pendingDeadline: { color: ticketColors.ink3, fontSize: 12, marginTop: 2 },
  pendingDeadlineUrgent: { color: ticketColors.bad, fontWeight: '700' },
  mpPaidHint: {
    backgroundColor: 'rgba(91,155,213,0.12)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#5B9BD5',
    borderRadius: 10,
    padding: 10,
  },
  mpPaidHintText: {
    color: ticketColors.ink2,
    fontSize: 12,
    lineHeight: 17,
  },

  primaryCtaDesk: {
    backgroundColor: ticketColors.gold,
    borderRadius: 14,
    minHeight: 80,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  primaryCtaText: {
    color: ticketColors.goldInk,
    fontSize: 18,
    fontWeight: '900',
    letterSpacing: 1,
  },
  primaryCtaSub: { flexDirection: 'row', alignItems: 'center', gap: 2 },
  primaryCtaSubText: {
    color: ticketColors.goldInk,
    fontSize: 11,
    fontWeight: '600',
    opacity: 0.7,
    letterSpacing: 0.4,
  },
  rejectCta: {
    borderWidth: 1,
    borderColor: ticketColors.bad,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center',
  },
  rejectCtaText: { color: ticketColors.bad, fontSize: 15, fontWeight: '700' },
  btnDisabled: { opacity: 0.6 },
  payoutBox: {
    borderWidth: 1,
    borderColor: ticketColors.gold,
    borderRadius: 10,
    padding: 14,
    gap: 8,
  },
  payoutTitle: {
    color: ticketColors.gold,
    fontSize: 15,
    fontWeight: '700',
  },
  payoutLine: {
    color: ticketColors.ink2,
    fontSize: 13,
  },
  payoutLink: {
    color: ticketColors.ok,
    fontSize: 14,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },

  errorText: { color: ticketColors.bad, fontSize: 16, marginBottom: 16, textAlign: 'center' },
  backBtn: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: ticketColors.paper2,
  },
  backBtnText: { color: ticketColors.ink, fontSize: 14, fontWeight: '600' },
});
